<template>
  <main class="home-page">
    <header class="home-header">
      <router-link to="/" class="brand" aria-label="Ir para a página inicial">
        <span class="brand-mark">F</span>
        <span>Fin</span>
      </router-link>

      <nav class="home-nav" aria-label="Navegação principal">
        <a href="#como-funciona">Como funciona</a>
        <a href="#recursos">Recursos</a>
        <a href="#planos">Planos</a>
      </nav>

      <div class="header-actions">
        <router-link to="/login" class="header-link">Entrar</router-link>
        <router-link :to="registerRoute" class="header-cta">Começar</router-link>
      </div>
    </header>

    <section class="hero">
      <div class="hero-content">
        <span class="eyebrow">Finanças por WhatsApp</span>
        <h1 aria-label="O Fin registra gastos, categoriza tudo, resume o mês, lembra vencimentos e organiza entradas para você.">
          <span class="hero-title__line">
            O Fin
            <span class="hero-title__dynamic-slot" aria-hidden="true">
              <span class="hero-title__measure">{{ heroMeasurePhrase }}</span>
              <span class="hero-title__animated">
                <span class="hero-title__dynamic">{{ typedHeroPhrase }}</span>
                <span class="hero-title__caret"></span>
              </span>
            </span>
          </span>
        </h1>
        <p>
          Transforme mensagens simples em lançamentos, categorias, compromissos e indicadores claros para acompanhar
          o mês sem planilhas confusas.
        </p>

        <div class="hero-actions">
          <router-link :to="registerRoute" class="primary-button">
            Começar agora
            <i class="ph ph-arrow-right"></i>
          </router-link>
          <router-link to="/login" class="secondary-button">Já tenho conta</router-link>
        </div>

        <div class="trust-strip" aria-label="Destaques do produto">
          <span><i class="ph ph-whatsapp-logo"></i> WhatsApp</span>
          <span><i class="ph ph-sparkle"></i> IA</span>
          <span><i class="ph ph-chart-line-up"></i> Tempo real</span>
        </div>
      </div>

      <aside class="app-preview" aria-label="Prévia do aplicativo financeiro">
        <div class="phone-shell">
          <div class="phone-top">
            <span>Hoje</span>
            <strong>Atualizado</strong>
          </div>

          <div class="message-bubble">
            <span class="message-bubble__icon"><i class="ph ph-whatsapp-logo"></i></span>
            <p>Anota 48,90 do almoço de hoje</p>
          </div>

          <div class="result-card">
            <div>
              <span>Lançamento criado</span>
              <strong>R$ 48,90</strong>
            </div>
            <i class="ph ph-check-circle"></i>
          </div>

          <div class="preview-actions">
            <span><i class="ph ph-plus"></i> Novo gasto</span>
            <span><i class="ph ph-receipt"></i> Lançamentos</span>
          </div>

          <div class="mini-dashboard">
            <div>
              <small>Gastos em junho</small>
              <strong>R$ 2.847,90</strong>
            </div>
            <div class="mini-row">
              <span>Mercado</span>
              <b>42%</b>
            </div>
            <div class="progress-track">
              <span style="width: 74%"></span>
            </div>
          </div>
        </div>
      </aside>
    </section>

    <section id="como-funciona" class="section flow-section">
      <div class="section-heading">
        <span class="eyebrow">Como funciona</span>
        <h2>Da frase solta ao financeiro organizado</h2>
        <p>O caminho foi pensado para parecer natural para quem usa e confiável para quem acompanha.</p>
      </div>

      <div class="steps">
        <article>
          <span class="step-icon"><i class="ph ph-chat-circle-text"></i></span>
          <small>Passo 1</small>
          <h3>Envie como você fala</h3>
          <p>O cliente manda uma mensagem simples pelo WhatsApp, sem precisar abrir planilha ou preencher formulário.</p>
        </article>
        <article>
          <span class="step-icon"><i class="ph ph-sparkle"></i></span>
          <small>Passo 2</small>
          <h3>A IA organiza</h3>
          <p>Valor, descrição, categoria e data são interpretados e salvos como lançamento financeiro.</p>
        </article>
        <article>
          <span class="step-icon"><i class="ph ph-chart-pie-slice"></i></span>
          <small>Passo 3</small>
          <h3>O app resume tudo</h3>
          <p>Dashboard, gastos, fixos e pagamentos ficam prontos para leitura rápida, com cara de aplicativo.</p>
        </article>
      </div>
    </section>

    <section id="recursos" class="section product-section">
      <div class="product-card product-card--featured">
        <span class="eyebrow">Experiência de app</span>
        <h2>O essencial aparece primeiro. O detalhe fica a um toque.</h2>
        <p>
          A home apresenta o mesmo jeito de usar que o cliente encontra depois do login: ações óbvias, cartões limpos
          e leitura rápida no celular.
        </p>
      </div>

      <div class="feature-grid">
        <article>
          <span><i class="ph ph-gauge"></i></span>
          <h3>Dashboard direto</h3>
          <p>Total do mês, comparação e atalhos para agir sem perder tempo.</p>
        </article>
        <article>
          <span><i class="ph ph-arrows-down-up"></i></span>
          <h3>Gastos e entradas</h3>
          <p>Histórico com filtros, detalhes e a mensagem original enviada para a IA.</p>
        </article>
        <article>
          <span><i class="ph ph-calendar-check"></i></span>
          <h3>Compromissos fixos</h3>
          <p>Agenda de vencimentos com visão de todos, vencidos e próximos pagamentos.</p>
        </article>
        <article>
          <span><i class="ph ph-credit-card"></i></span>
          <h3>Planos e conta</h3>
          <p>Pagamentos, configurações e equipe com visual simples para o cliente final.</p>
        </article>
      </div>
    </section>

    <section id="planos" class="section plans-section">
      <div class="section-heading section-heading--inline">
        <div>
          <span class="eyebrow">Planos</span>
          <h2>Escolha o melhor começo para sua rotina</h2>
        </div>
        <router-link :to="registerRoute" class="small-action">
          Criar conta
          <i class="ph ph-arrow-right"></i>
        </router-link>
      </div>

      <div v-if="isLoadingPlans" class="plans-grid" aria-label="Carregando planos">
        <article v-for="index in 3" :key="index" class="plan-card plan-card--loading">
          <span class="skeleton skeleton-title"></span>
          <span class="skeleton skeleton-text"></span>
          <span class="skeleton skeleton-price"></span>
          <span class="skeleton skeleton-button"></span>
        </article>
      </div>

      <div v-else-if="plansError" class="plans-feedback plans-feedback--error">
        <span><i class="ph ph-warning-circle"></i></span>
        <div>
          <h3>Não foi possível carregar os planos</h3>
          <p>Tente novamente ou continue criando sua conta para escolher o plano depois.</p>
        </div>
        <button type="button" @click="loadPlans">Tentar novamente</button>
      </div>

      <div v-else-if="!plans.length" class="plans-feedback">
        <span><i class="ph ph-leaf"></i></span>
        <div>
          <h3>Planos em atualização</h3>
          <p>Você ainda pode criar sua conta e concluir a escolha do plano na sequência.</p>
        </div>
        <router-link to="/register">Criar conta</router-link>
      </div>

      <div v-else class="plans-grid">
        <article v-for="plan in plans" :key="plan.id" class="plan-card">
          <div class="plan-card__top">
            <span class="plan-icon"><i class="ph ph-rocket-launch"></i></span>
            <h3>{{ plan.name }}</h3>
          </div>
          <p>{{ plan.description || "Plano para acompanhar sua rotina financeira pelo aplicativo." }}</p>
          <strong>{{ formatPlanPrice(plan) }}</strong>
          <ul v-if="planFeatures(plan).length">
            <li v-for="feature in planFeatures(plan)" :key="feature">
              <i class="ph ph-check-circle"></i>
              {{ feature }}
            </li>
          </ul>
          <router-link :to="planRegisterRoute(plan)" class="plan-button">
            Contratar
            <i class="ph ph-arrow-right"></i>
          </router-link>
        </article>
      </div>
    </section>

    <footer class="home-footer">
      <div class="footer-grid">
        <div class="footer-brand">
          <router-link to="/" class="brand">
            <span class="brand-mark">F</span>
            <span>Fin</span>
          </router-link>
          <p>
            Controle financeiro por WhatsApp, lançamentos organizados e métricas prontas para acompanhar o mês.
          </p>
        </div>

        <div class="footer-column">
          <h3>Produto</h3>
          <a href="#como-funciona">Como funciona</a>
          <a href="#recursos">Recursos</a>
          <a href="#planos">Planos</a>
        </div>

        <div class="footer-column">
          <h3>Conta</h3>
          <router-link to="/login">Entrar</router-link>
          <router-link to="/register">Criar conta</router-link>
          <router-link to="/forgot-password">Esqueci minha senha</router-link>
          <router-link to="/terms">Termos de uso</router-link>
        </div>

        <div class="footer-column">
          <h3>Contato</h3>
          <a href="mailto:contato@sanvault.com.br">contato@sanvault.com.br</a>
          <a href="https://wa.me/5511915100084" target="_blank" rel="noopener noreferrer">(11) 9 1510-0084</a>
        </div>
      </div>

      <div class="footer-bottom">
        <span>© {{ currentYear }} Fin. Todos os direitos reservados.</span>
        <span>API financeira integrada ao WhatsApp.</span>
      </div>
    </footer>
  </main>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import api from "../../config/axios.js";

interface HomePlan {
  id: string | number;
  name: string;
  description?: string;
  price_formatted?: string;
  priceFormatted?: string;
  price?: string | number;
  features?: string[];
}

type PlansResponse = HomePlan[] | { value?: HomePlan[] };

export default defineComponent({
  name: "HomePage",
  data() {
    return {
      plans: [] as HomePlan[],
      isLoadingPlans: false,
      plansError: false,
      heroPhrases: [
        "registra gastos para você",
        "categoriza tudo para você",
        "resume o mês para você",
        "lembra vencimentos para você",
        "organiza entradas para você",
      ] as string[],
      typedHeroPhrase: "registra gastos para você",
      heroPhraseIndex: 0,
      heroCharIndex: "registra gastos para você".length,
      isDeletingHeroPhrase: false,
      heroTypingTimer: undefined as number | undefined,
    };
  },
  computed: {
    registerRoute(): string {
      return this.plans.length ? this.planRegisterRoute(this.plans[0]) : "/register";
    },
    currentYear(): number {
      return new Date().getFullYear();
    },
    heroMeasurePhrase(): string {
      return this.heroPhrases.reduce((longest, phrase) => (phrase.length > longest.length ? phrase : longest), "");
    },
  },
  async mounted() {
    this.startHeroTyping();
    await this.loadPlans();
  },
  beforeUnmount() {
    if (this.heroTypingTimer) {
      window.clearTimeout(this.heroTypingTimer);
    }
  },
  methods: {
    startHeroTyping(): void {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        this.typedHeroPhrase = this.heroPhrases[0];
        return;
      }

      this.scheduleHeroTyping(1400);
    },
    scheduleHeroTyping(delay: number): void {
      if (this.heroTypingTimer) {
        window.clearTimeout(this.heroTypingTimer);
      }

      this.heroTypingTimer = window.setTimeout(() => this.tickHeroTyping(), delay);
    },
    tickHeroTyping(): void {
      const currentPhrase = this.heroPhrases[this.heroPhraseIndex];

      if (this.isDeletingHeroPhrase) {
        this.heroCharIndex = Math.max(0, this.heroCharIndex - 1);
        this.typedHeroPhrase = currentPhrase.slice(0, this.heroCharIndex);

        if (this.heroCharIndex === 0) {
          this.isDeletingHeroPhrase = false;
          this.heroPhraseIndex = (this.heroPhraseIndex + 1) % this.heroPhrases.length;
          this.scheduleHeroTyping(240);
          return;
        }

        this.scheduleHeroTyping(44);
        return;
      }

      const nextPhrase = this.heroPhrases[this.heroPhraseIndex];
      this.heroCharIndex = Math.min(nextPhrase.length, this.heroCharIndex + 1);
      this.typedHeroPhrase = nextPhrase.slice(0, this.heroCharIndex);

      if (this.heroCharIndex === nextPhrase.length) {
        this.isDeletingHeroPhrase = true;
        this.scheduleHeroTyping(1800);
        return;
      }

      this.scheduleHeroTyping(82);
    },
    async loadPlans(): Promise<void> {
      this.isLoadingPlans = true;
      this.plansError = false;

      try {
        const response = await api.axios.get<PlansResponse>("/api/plans");
        this.plans = this.normalizePlans(response.data);
      } catch (error) {
        this.plans = [];
        this.plansError = true;
      } finally {
        this.isLoadingPlans = false;
      }
    },
    normalizePlans(payload: PlansResponse): HomePlan[] {
      if (Array.isArray(payload)) {
        return payload;
      }

      if (Array.isArray(payload?.value)) {
        return payload.value;
      }

      return [];
    },
    formatPlanPrice(plan: HomePlan): string {
      if (plan.price_formatted) {
        return plan.price_formatted;
      }

      if (plan.priceFormatted) {
        return plan.priceFormatted;
      }

      if (typeof plan.price === "number") {
        return new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
        }).format(plan.price);
      }

      return "Consulte";
    },
    planFeatures(plan: HomePlan): string[] {
      return Array.isArray(plan.features) ? plan.features : [];
    },
    planRegisterRoute(plan: HomePlan): string {
      return `/register?plan=${encodeURIComponent(String(plan.id))}`;
    },
  },
});
</script>

<style scoped>
.home-page {
  --home-bg: #f1f0f8;
  --home-surface: #ffffff;
  --home-soft: #f7f7fb;
  --home-ink: #181729;
  --home-muted: #747084;
  --home-border: #e6e2ef;
  --home-green: #06a77d;
  --home-green-dark: #075747;
  --home-green-deep: #093a34;
  --home-green-soft: #e5fbf5;
  --home-shadow: 0 24px 70px rgba(30, 28, 54, 0.1);
  background:
    radial-gradient(circle at 20% 0%, rgba(6, 167, 125, 0.13), transparent 32rem),
    linear-gradient(180deg, #f7f5fc 0%, var(--home-bg) 100%);
  color: var(--home-ink);
  min-height: 100vh;
  overflow-x: hidden;
}

.home-header {
  align-items: center;
  backdrop-filter: blur(18px);
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(230, 226, 239, 0.86);
  border-radius: 999px;
  box-shadow: 0 16px 42px rgba(30, 28, 54, 0.08);
  display: flex;
  gap: 18px;
  justify-content: space-between;
  left: max(16px, env(safe-area-inset-left));
  padding: 8px 10px 8px 12px;
  position: sticky;
  right: max(16px, env(safe-area-inset-right));
  top: max(12px, env(safe-area-inset-top));
  width: calc(100% - max(16px, env(safe-area-inset-left)) - max(16px, env(safe-area-inset-right)));
  z-index: 20;
}

.brand,
.home-nav a,
.header-link,
.header-cta,
.primary-button,
.secondary-button,
.small-action,
.plan-button,
.plans-feedback a,
.plans-feedback button {
  align-items: center;
  display: inline-flex;
  gap: 8px;
  justify-content: center;
  min-height: 44px;
  text-decoration: none;
}

.brand {
  color: var(--home-ink);
  font-size: 17px;
  font-weight: 900;
}

.brand-mark {
  align-items: center;
  background: linear-gradient(145deg, var(--home-green), #018967);
  border-radius: 14px;
  color: #fff;
  display: inline-flex;
  font-weight: 900;
  height: 38px;
  justify-content: center;
  width: 38px;
}

.home-nav {
  align-items: center;
  display: flex;
  gap: 6px;
}

.home-nav a,
.header-link {
  border-radius: 999px;
  color: var(--home-muted);
  font-size: 13px;
  font-weight: 800;
  padding: 0 14px;
}

.home-nav a:hover,
.header-link:hover {
  background: var(--home-soft);
  color: var(--home-green-dark);
}

.header-actions {
  align-items: center;
  display: flex;
  gap: 8px;
}

.header-cta {
  background: var(--home-green);
  border-radius: 999px;
  color: #fff;
  font-size: 13px;
  font-weight: 900;
  padding: 0 18px;
}

.hero {
  align-items: center;
  display: grid;
  gap: clamp(26px, 6vw, 72px);
  grid-template-columns: minmax(0, 1fr) minmax(300px, 460px);
  min-height: calc(100vh - 82px);
  padding: clamp(42px, 7vw, 86px) 6vw clamp(56px, 8vw, 96px);
}

.hero-content {
  max-width: 980px;
}

.eyebrow {
  color: #007f61;
  display: inline-flex;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.hero h1 {
  color: var(--home-ink);
  font-size: clamp(36px, 4.8vw, 62px);
  letter-spacing: -0.055em;
  line-height: 1;
  margin: 12px 0 18px;
  max-width: min(1120px, 100%);
}

.hero-title__line {
  align-items: flex-start;
  display: flex;
  flex-wrap: nowrap;
  gap: 0 0.2em;
}

.hero-title__dynamic-slot {
  color: var(--home-green-dark);
  display: inline-grid;
  flex: 1 1 auto;
  grid-template-areas: "dynamic";
  min-width: 0;
  vertical-align: baseline;
}

.hero-title__measure,
.hero-title__animated {
  grid-area: dynamic;
}

.hero-title__measure {
  pointer-events: none;
  visibility: hidden;
  white-space: normal;
}

.hero-title__animated {
  display: block;
  min-width: 0;
  white-space: normal;
}

.hero-title__dynamic {
  color: var(--home-green-dark);
  display: inline;
  position: relative;
}

.hero-title__caret {
  animation: caretBlink 0.82s steps(2, end) infinite;
  background: var(--home-green);
  border-radius: 999px;
  display: inline-block;
  height: 0.78em;
  margin: 0 0.06em;
  transform: translateY(0.08em);
  width: 0.065em;
}

.hero p,
.section-heading p,
.product-card p,
.feature-grid p,
.steps p,
.plan-card p,
.plans-feedback p,
.footer-brand p {
  color: var(--home-muted);
  font-size: 15px;
  line-height: 1.65;
}

.hero p {
  font-size: clamp(16px, 2vw, 18px);
  max-width: 610px;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 28px;
}

.primary-button,
.secondary-button,
.small-action,
.plan-button,
.plans-feedback a,
.plans-feedback button {
  border-radius: 16px;
  font-weight: 900;
  padding: 0 18px;
}

.primary-button,
.plan-button {
  background: var(--home-green);
  border: 1px solid var(--home-green);
  color: #fff;
  box-shadow: 0 14px 32px rgba(6, 167, 125, 0.22);
}

.secondary-button,
.small-action,
.plans-feedback a,
.plans-feedback button {
  background: var(--home-surface);
  border: 1px solid var(--home-border);
  color: var(--home-green-dark);
}

.trust-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 22px;
}

.trust-strip span {
  align-items: center;
  background: rgba(255, 255, 255, 0.74);
  border: 1px solid rgba(6, 167, 125, 0.13);
  border-radius: 999px;
  color: var(--home-green-dark);
  display: inline-flex;
  font-size: 12px;
  font-weight: 900;
  gap: 7px;
  min-height: 34px;
  padding: 0 12px;
}

.app-preview {
  display: flex;
  justify-content: center;
}

.phone-shell {
  animation: phoneFloat 8s ease-in-out infinite;
  background: linear-gradient(160deg, #0c6c60 0%, var(--home-green-deep) 56%, #102420 100%);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 32px;
  box-shadow: 0 34px 90px rgba(8, 66, 56, 0.28);
  color: #fff;
  overflow: hidden;
  padding: 22px;
  position: relative;
  width: min(100%, 420px);
}

.phone-shell::before,
.phone-shell::after {
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  content: "";
  height: 210px;
  position: absolute;
  right: -76px;
  top: -54px;
  width: 210px;
}

.phone-shell::after {
  bottom: -84px;
  height: 170px;
  left: -76px;
  top: auto;
  width: 170px;
}

.phone-top,
.result-card,
.preview-actions,
.mini-dashboard,
.message-bubble {
  position: relative;
  z-index: 1;
}

.phone-top {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.phone-top span {
  color: rgba(255, 255, 255, 0.72);
  font-size: 13px;
  font-weight: 800;
}

.phone-top strong {
  align-items: center;
  background: rgba(255, 255, 255, 0.11);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 999px;
  display: inline-flex;
  font-size: 11px;
  min-height: 30px;
  padding: 0 11px;
  text-transform: uppercase;
}

.message-bubble {
  align-items: flex-start;
  animation: previewCardFloat 7s ease-in-out infinite;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 22px 22px 22px 8px;
  display: flex;
  gap: 12px;
  padding: 14px;
}

.message-bubble p {
  color: #fff;
  font-size: 14px;
  line-height: 1.4;
  margin: 0;
}

.message-bubble__icon,
.result-card i,
.step-icon,
.feature-grid article > span,
.plan-icon,
.plans-feedback > span {
  align-items: center;
  display: inline-flex;
  justify-content: center;
}

.message-bubble__icon {
  background: rgba(6, 167, 125, 0.26);
  border-radius: 12px;
  color: #9ff7df;
  flex: 0 0 34px;
  height: 34px;
}

.result-card {
  align-items: center;
  animation: previewCardFloat 7.8s ease-in-out infinite 0.45s;
  background: linear-gradient(145deg, rgba(226, 250, 242, 0.9), rgba(189, 231, 219, 0.76));
  border: 1px solid rgba(167, 221, 206, 0.72);
  border-radius: 24px;
  color: #12372f;
  display: flex;
  justify-content: space-between;
  margin-top: 14px;
  padding: 18px;
}

.result-card span,
.mini-dashboard small {
  color: rgba(18, 55, 47, 0.68);
  display: block;
  font-size: 12px;
  font-weight: 800;
  margin-bottom: 4px;
}

.result-card strong {
  display: block;
  font-size: 32px;
  letter-spacing: -0.06em;
}

.result-card i {
  background: rgba(6, 167, 125, 0.16);
  border-radius: 16px;
  color: var(--home-green);
  font-size: 28px;
  height: 52px;
  width: 52px;
}

.preview-actions {
  animation: previewCardFloat 8.2s ease-in-out infinite 0.75s;
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr 1fr;
  margin-top: 14px;
}

.preview-actions span {
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 18px;
  display: inline-flex;
  font-size: 12px;
  font-weight: 900;
  gap: 8px;
  justify-content: center;
  min-height: 54px;
}

.mini-dashboard {
  animation: previewCardFloat 8.6s ease-in-out infinite 1s;
  background: linear-gradient(145deg, rgba(226, 250, 242, 0.86), rgba(195, 231, 221, 0.72));
  border: 1px solid rgba(167, 221, 206, 0.66);
  border-radius: 24px;
  color: #12372f;
  margin-top: 14px;
  padding: 18px;
}

.mini-dashboard strong {
  font-size: 26px;
  letter-spacing: -0.05em;
}

.mini-row {
  align-items: center;
  color: rgba(18, 55, 47, 0.68);
  display: flex;
  font-size: 13px;
  font-weight: 800;
  justify-content: space-between;
  margin-top: 18px;
}

.progress-track {
  background: #eceaf3;
  border-radius: 999px;
  height: 7px;
  margin-top: 8px;
  overflow: hidden;
}

.progress-track span {
  background: var(--home-green);
  border-radius: inherit;
  display: block;
  height: 100%;
}

.section {
  padding: clamp(48px, 7vw, 82px) 6vw;
}

.section-heading {
  margin-bottom: 24px;
  max-width: 720px;
}

.section-heading--inline {
  align-items: flex-end;
  display: flex;
  justify-content: space-between;
  gap: 18px;
  max-width: none;
}

.section-heading h2,
.product-card h2 {
  color: var(--home-ink);
  font-size: clamp(28px, 4vw, 46px);
  letter-spacing: -0.045em;
  line-height: 1.04;
  margin: 8px 0 0;
}

.steps,
.feature-grid,
.plans-grid {
  display: grid;
  gap: 16px;
}

.steps {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.steps article,
.feature-grid article,
.plan-card,
.plans-feedback,
.product-card {
  background: var(--home-surface);
  border: 1px solid var(--home-border);
  border-radius: 24px;
  box-shadow: 0 16px 42px rgba(30, 28, 54, 0.06);
}

.steps article,
.feature-grid article,
.plan-card {
  padding: 22px;
}

.step-icon,
.feature-grid article > span,
.plan-icon,
.plans-feedback > span {
  background: var(--home-green-soft);
  border-radius: 16px;
  color: var(--home-green);
  font-size: 24px;
  height: 54px;
  width: 54px;
}

.steps small {
  color: var(--home-green);
  display: block;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  margin-top: 18px;
  text-transform: uppercase;
}

.steps h3,
.feature-grid h3,
.plan-card h3,
.plans-feedback h3 {
  color: var(--home-ink);
  font-size: 20px;
  letter-spacing: -0.025em;
  margin: 8px 0;
}

.product-section {
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(280px, 0.9fr) minmax(0, 1.1fr);
}

.product-card {
  padding: clamp(24px, 4vw, 34px);
}

.product-card--featured {
  background:
    radial-gradient(circle at 100% 0%, rgba(255, 255, 255, 0.14), transparent 16rem),
    linear-gradient(160deg, #0d6f62 0%, #073e36 100%);
  border-color: transparent;
  color: #fff;
}

.product-card--featured .eyebrow,
.product-card--featured h2,
.product-card--featured p {
  color: #fff;
}

.product-card--featured p {
  opacity: 0.78;
}

.feature-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.plans-grid {
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 320px), 390px));
  justify-content: center;
}

.plan-card {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-height: 100%;
  width: 100%;
}

.plan-card__top {
  align-items: center;
  display: flex;
  gap: 12px;
}

.plan-card h3 {
  margin: 0;
}

.plan-card strong {
  color: var(--home-ink);
  display: block;
  font-size: 34px;
  letter-spacing: -0.055em;
  line-height: 1;
}

.plan-card ul {
  display: flex;
  flex-direction: column;
  gap: 9px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.plan-card li {
  align-items: flex-start;
  color: var(--home-muted);
  display: flex;
  font-size: 14px;
  gap: 8px;
  line-height: 1.4;
}

.plan-card li i {
  color: var(--home-green);
  font-size: 18px;
  margin-top: 1px;
}

.plan-button {
  margin-top: auto;
}

.plans-feedback {
  align-items: center;
  display: grid;
  gap: 16px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  padding: 18px;
}

.plans-feedback p,
.plans-feedback h3 {
  margin-bottom: 0;
}

.plans-feedback--error > span {
  background: #fff0ee;
  color: #c74f4a;
}

.plans-feedback button {
  cursor: pointer;
}

.skeleton {
  animation: pulse 1.25s ease-in-out infinite;
  background: linear-gradient(90deg, #eeecf4, #f8f7fb, #eeecf4);
  background-size: 220% 100%;
  border-radius: 999px;
  display: block;
}

.skeleton-title {
  height: 28px;
  width: 62%;
}

.skeleton-text {
  height: 16px;
  width: 100%;
}

.skeleton-price {
  height: 42px;
  width: 48%;
}

.skeleton-button {
  height: 46px;
  margin-top: auto;
  width: 100%;
}

.home-footer {
  background: #0f1720;
  color: #d9dde5;
  padding: 56px 6vw calc(26px + env(safe-area-inset-bottom));
}

.footer-grid {
  display: grid;
  gap: 34px;
  grid-template-columns: minmax(260px, 1.4fr) repeat(3, minmax(150px, 1fr));
}

.footer-brand .brand {
  color: #fff;
}

.footer-brand p {
  color: #aeb7c4;
  margin: 18px 0 0;
  max-width: 390px;
}

.footer-column {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.footer-column h3 {
  color: #fff;
  font-size: 14px;
  font-weight: 900;
  margin: 0 0 8px;
}

.footer-column a {
  color: #aeb7c4;
  text-decoration: none;
}

.footer-column a:hover {
  color: #fff;
}

.footer-bottom {
  align-items: center;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: #aeb7c4;
  display: flex;
  font-size: 13px;
  gap: 16px;
  justify-content: space-between;
  margin-top: 42px;
  padding-top: 22px;
}

@keyframes pulse {
  0% {
    background-position: 0% 50%;
  }

  100% {
    background-position: -220% 50%;
  }
}

@keyframes caretBlink {
  0%,
  45% {
    opacity: 1;
  }

  46%,
  100% {
    opacity: 0;
  }
}

@keyframes phoneFloat {
  0%,
  100% {
    transform: translate3d(0, 0, 0) rotate(0deg);
  }

  50% {
    transform: translate3d(0, -10px, 0) rotate(-0.35deg);
  }
}

@keyframes previewCardFloat {
  0%,
  100% {
    transform: translate3d(0, 0, 0);
  }

  50% {
    transform: translate3d(0, -4px, 0);
  }
}

body.dark .home-page {
  --home-bg: #1f1e2e;
  --home-surface: #29283b;
  --home-soft: #343347;
  --home-ink: #f5f4fb;
  --home-muted: #aaa8ba;
  --home-border: #3b394e;
  --home-green-soft: rgba(6, 167, 125, 0.16);
  background:
    radial-gradient(circle at 20% 0%, rgba(6, 167, 125, 0.16), transparent 32rem),
    linear-gradient(180deg, #232234 0%, #1f1e2e 100%);
}

body.dark .home-page .home-header,
body.dark .home-page .trust-strip span,
body.dark .home-page .secondary-button,
body.dark .home-page .small-action,
body.dark .home-page .plans-feedback a,
body.dark .home-page .plans-feedback button {
  background: rgba(41, 40, 59, 0.86);
}

body.dark .home-page .home-header {
  border-color: rgba(255, 255, 255, 0.08);
}

body.dark .home-page .eyebrow,
body.dark .home-page .hero-title__dynamic,
body.dark .home-page .home-nav a:hover,
body.dark .home-page .header-link:hover,
body.dark .home-page .secondary-button,
body.dark .home-page .small-action,
body.dark .home-page .plans-feedback a,
body.dark .home-page .plans-feedback button {
  color: #60d5b5;
}

body.dark .home-page .result-card,
body.dark .home-page .mini-dashboard {
  background: linear-gradient(145deg, rgba(31, 91, 79, 0.86), rgba(35, 64, 61, 0.9));
  border-color: rgba(96, 213, 181, 0.22);
  color: #eefdf8;
}

body.dark .home-page .result-card span,
body.dark .home-page .mini-dashboard small,
body.dark .home-page .mini-row {
  color: rgba(238, 253, 248, 0.68);
}

body.dark .home-page .progress-track,
body.dark .home-page .skeleton {
  background: #3a394d;
}

body.dark .home-page .plans-feedback--error > span {
  background: rgba(199, 79, 74, 0.17);
  color: #ffaaa4;
}

@media (max-width: 1120px) {
  .home-nav {
    display: none;
  }

  .hero,
  .product-section,
  .footer-grid {
    grid-template-columns: 1fr;
  }

  .app-preview {
    justify-content: flex-start;
  }

  .plans-grid {
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 320px), 390px));
  }
}

@media (max-width: 760px) {
  .home-header {
    border-radius: 24px;
  }

  .header-link {
    display: none;
  }

  .hero {
    min-height: auto;
    padding-left: 16px;
    padding-right: 16px;
  }

  .hero h1 {
    font-size: clamp(19px, 5.2vw, 23px);
    min-height: 2em;
  }

  .hero-title__line {
    align-items: flex-start;
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 0 0.2em;
  }

  .hero-title__dynamic-slot {
    display: grid;
    line-height: 1;
    min-height: 1em;
    max-width: 100%;
    width: auto;
  }

  .hero-title__measure,
  .hero-title__animated {
    white-space: normal;
  }

  .hero-title__animated {
    min-height: 1em;
  }

  .primary-button,
  .secondary-button {
    flex: 1 1 100%;
  }

  .phone-shell {
    border-radius: 26px;
    padding: 18px;
  }

  .preview-actions {
    grid-template-columns: 1fr;
  }

  .section {
    padding-left: 16px;
    padding-right: 16px;
  }

  .section-heading--inline {
    align-items: flex-start;
    flex-direction: column;
  }

  .steps,
  .feature-grid,
  .plans-grid {
    grid-template-columns: 1fr;
  }

  .plans-feedback {
    align-items: flex-start;
    grid-template-columns: 1fr;
  }

  .plans-feedback > span {
    height: 48px;
    width: 48px;
  }

  .footer-grid {
    grid-template-columns: 1fr;
  }

  .footer-bottom {
    align-items: flex-start;
    flex-direction: column;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero-title__caret {
    animation: none;
    opacity: 0;
  }

  .phone-shell,
  .message-bubble,
  .result-card,
  .preview-actions,
  .mini-dashboard {
    animation: none;
  }
}

@media (max-width: 390px) {
  .home-header {
    left: 10px;
    right: 10px;
    width: calc(100% - 20px);
  }

  .brand {
    font-size: 0;
    gap: 0;
  }

  .brand-mark {
    font-size: 16px;
    height: 40px;
    width: 40px;
  }

  .header-cta {
    padding: 0 14px;
  }

  .result-card {
    align-items: flex-start;
    flex-direction: column;
    gap: 14px;
  }
}
</style>
